import React, { useState } from 'react';
import { 
    PlusIcon, 
    TrashIcon, 
    ChevronUpIcon, 
    ChevronDownIcon,
    DocumentPlusIcon,
    SparklesIcon,
    CalendarIcon,
    ShieldCheckIcon,
    SwatchIcon
} from '@heroicons/react/24/outline';
import type { ReportSection, ReportSectionType, FormState, CompositeReportRequest, ReportStyle, ClassificationLevel } from '@types';
import { REPORT_MODULES } from './constants';
import ReportSectionForm from './ReportSectionForm';

interface ReportBuilderProps {
    isLoading: boolean;
    onSubmit: (event: React.FormEvent, reportData: CompositeReportRequest) => void;
    inline?: boolean;
    // Controlled state from parent (ReportHub) to survive re-renders
    sections?: ReportSection[];
    onSectionsChange?: (sections: ReportSection[]) => void;
}

const ReportBuilder: React.FC<ReportBuilderProps> = ({ isLoading, onSubmit, sections: externalSections, onSectionsChange }) => {
    const [internalSections, setInternalSections] = useState<ReportSection[]>([]);
    // Use controlled sections if provided, else fall back to local state
    const sections = externalSections ?? internalSections;
    const setSections = (next: ReportSection[] | ((prev: ReportSection[]) => ReportSection[])) => {
        const resolved = typeof next === 'function' ? next(sections) : next;
        if (onSectionsChange) {
            onSectionsChange(resolved);
        } else {
            setInternalSections(resolved);
        }
    };
    const [reportTitle, setReportTitle] = useState('Strategic Intelligence Briefing');
    const [selectedModule, setSelectedModule] = useState<ReportSectionType | ''>('');
    
    // Advanced Filters
    const [timeframe, setTimeframe] = useState<'historical' | 'recent' | 'current'>('recent');
    const [reportStyle, setReportStyle] = useState<ReportStyle>('executive');
    const [classification, setClassification] = useState<ClassificationLevel>('CONFIDENTIAL // PROPRIETARY');

    const addSection = () => {
        if (!selectedModule) return;
        const newSection: ReportSection = {
            id: `section-${Date.now()}`,
            type: selectedModule,
            title: REPORT_MODULES[selectedModule]?.name || selectedModule,
            formData: {},
        };
        setSections([...sections, newSection]);
        setSelectedModule('');
    };

    const removeSection = (id: string) => {
        setSections(sections.filter(section => section.id !== id));
    };

    const updateSectionForm = (id: string, updatedFormData: Partial<FormState>) => {
        setSections(sections.map(section =>
            section.id === id ? { ...section, formData: updatedFormData } : section
        ));
    };
    
    const moveSection = (index: number, direction: 'up' | 'down') => {
        const newSections = [...sections];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        if (targetIndex >= 0 && targetIndex < newSections.length) {
            [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
            setSections(newSections);
        }
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        const reportData: CompositeReportRequest = {
            reportTitle,
            reportStyle,
            classification,
            sections,
            metadata: {
                timeframe,
                generatedAt: new Date().toISOString(),
                version: '2026.4.0-ECC'
            }
        };
        onSubmit(e, reportData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Global Configuration Matrix */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="group">
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 group-focus-within:text-indigo-600 transition-colors">Strategic Designation</label>
                        <input
                            type="text"
                            value={reportTitle}
                            onChange={(e) => setReportTitle(e.target.value)}
                            className="w-full bg-white border-2 border-slate-100 rounded-2xl p-5 text-xl font-bold text-slate-900 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 outline-none transition-all placeholder:text-slate-300 shadow-sm"
                            placeholder="Enter report designation..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                <CalendarIcon className="h-3 w-3" />
                                Intelligence Timeframe
                            </label>
                            <select 
                                value={timeframe}
                                onChange={(e) => setTimeframe(e.target.value as any)}
                                className="w-full bg-white border-2 border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 focus:border-indigo-500 outline-none shadow-sm transition-all"
                            >
                                <option value="current">Real-time Analysis (Live)</option>
                                <option value="recent">Recent Activity (L30D)</option>
                                <option value="historical">Historical Statistics (L12M)</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                <SwatchIcon className="h-3 w-3" />
                                Branded Framework
                            </label>
                            <select 
                                value={reportStyle}
                                onChange={(e) => setReportStyle(e.target.value as any)}
                                className="w-full bg-white border-2 border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 focus:border-indigo-500 outline-none shadow-sm transition-all"
                            >
                                <option value="standard">Standard Analytical</option>
                                <option value="executive">Executive Strategic Hub</option>
                                <option value="detailed">Technical Comprehensive</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                <ShieldCheckIcon className="h-3 w-3" />
                                Security Protocol
                            </label>
                            <select 
                                value={classification}
                                onChange={(e) => setClassification(e.target.value)}
                                className="w-full bg-white border-2 border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 focus:border-indigo-500 outline-none shadow-sm transition-all"
                            >
                                <option value="UNCLASSIFIED">Unclassified / Public</option>
                                <option value="INTERNAL USE ONLY">Internal Use Only</option>
                                <option value="CONFIDENTIAL // PROPRIETARY">Confidential // Proprietary</option>
                                <option value="SECRET // RESTRICTED">Secret // Restricted</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Info Card / Guidelines */}
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-6 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-indigo-600 rounded-lg shadow-lg shadow-indigo-200">
                            <SparklesIcon className="h-4 w-4 text-white" />
                        </div>
                        <h4 className="text-xs font-black text-indigo-900 uppercase tracking-widest">ECC Generation Engine</h4>
                    </div>
                    <p className="text-[11px] text-indigo-700 font-medium leading-relaxed">
                        The Strategic Hub aggregates usage statistics, historical trends, and cross-suite analytics into a unified executive deliverable.
                    </p>
                    <div className="mt-4 pt-4 border-t border-indigo-100 flex items-center justify-between">
                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-tighter">System Integrity v4.2</span>
                        <div className="flex gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 delay-75 animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Analysis Matrix */}
            <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-2">
                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-[0.2em]">Intelligence Modules</h3>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-[9px] font-black text-slate-500">{sections.length}</span>
                    </div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Interactive Build Matrix</p>
                </div>
                
                <div className="space-y-4">
                    {sections.map((section, index) => (
                        <div key={section.id} className="bg-white border-2 border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:border-indigo-200 transition-all group">
                            <div className="bg-slate-50/50 border-b border-slate-100 px-6 py-4 flex items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-black text-[10px] shadow-lg shadow-slate-200">
                                        {index + 1}
                                    </div>
                                    <div>
                                        <h4 className="font-black text-slate-900 uppercase tracking-tighter text-sm">{section.title}</h4>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Active Analysis Component</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                                    <button type="button" onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-1.5 hover:bg-white rounded-md text-slate-600 disabled:opacity-20 transition-all border border-transparent hover:border-slate-200 shadow-sm">
                                        <ChevronUpIcon className="h-4 w-4" />
                                    </button>
                                     <button type="button" onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="p-1.5 hover:bg-white rounded-md text-slate-600 disabled:opacity-20 transition-all border border-transparent hover:border-slate-200 shadow-sm">
                                        <ChevronDownIcon className="h-4 w-4" />
                                    </button>
                                    <div className="w-px h-6 bg-slate-200 mx-1"></div>
                                    <button type="button" onClick={() => removeSection(section.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-all border border-transparent hover:border-red-100">
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="p-6 bg-white">
                                <ReportSectionForm section={section} onFormChange={updateSectionForm} />
                            </div>
                        </div>
                    ))}

                    {sections.length === 0 && (
                        <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50 group/empty cursor-pointer hover:bg-indigo-50/20 hover:border-indigo-200 transition-all" onClick={() => document.getElementById('module-selector')?.focus()}>
                            <div className="w-16 h-16 bg-white rounded-2xl shadow-xl border border-slate-100 flex items-center justify-center mx-auto mb-6 group-hover/empty:scale-110 transition-transform duration-500">
                                <DocumentPlusIcon className="h-8 w-8 text-slate-300 group-hover/empty:text-indigo-400" />
                            </div>
                            <p className="text-slate-900 font-black uppercase tracking-[0.3em] text-[10px]">Strategic Portfolio Empty</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase mt-2 max-w-[200px] mx-auto leading-relaxed">Initialize the matrix by integrating intelligence modules below</p>
                        </div>
                    )}
                </div>

                {/* Add Section Controls */}
                <div className="bg-slate-900 p-8 rounded-3xl shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                        <PlusIcon className="h-32 w-32 text-indigo-500 transform rotate-12" />
                    </div>
                    
                    <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                            <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">Integrated Intelligence Matrix</h4>
                        </div>
                        <div className="flex flex-col md:flex-row items-center gap-4">
                            <div className="relative w-full">
                                <select
                                    id="module-selector"
                                    value={selectedModule}
                                    onChange={(e) => setSelectedModule(e.target.value as ReportSectionType)}
                                    className="w-full bg-slate-800 border-none text-white text-sm font-bold rounded-2xl p-5 outline-none ring-1 ring-slate-700/50 focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer shadow-inner"
                                >
                                    <option value="" disabled>Select Strategic Component...</option>
                                    {Object.entries(REPORT_MODULES).map(([key, mod]) => (
                                        <option key={key} value={key} className="bg-slate-900 py-4">{mod.name}</option>
                                    ))}
                                </select>
                                <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <ChevronDownIcon className="h-4 w-4 text-slate-500" />
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={addSection}
                                disabled={!selectedModule}
                                className="w-full md:w-auto shrink-0 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 text-white font-black uppercase tracking-widest text-[11px] py-5 px-10 rounded-2xl transition-all shadow-xl shadow-indigo-900/40 flex items-center justify-center gap-3 hover:translate-y-[-2px] active:translate-y-0"
                            >
                                <PlusIcon className="h-4 w-4" />
                                Integrate Component
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Submission Layer */}
            <div className="mt-16 pt-10 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full border-2 border-slate-100 flex items-center justify-center text-slate-400">
                        <ShieldCheckIcon className="h-5 w-5" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Secure Submission</p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">ECC Verification Protocol v2026</p>
                    </div>
                </div>
                
                <button
                    type="submit"
                    disabled={isLoading || sections.length === 0}
                    className="w-full md:w-auto bg-slate-900 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-black uppercase tracking-[0.3em] text-xs py-6 px-16 rounded-2xl flex items-center justify-center transition-all shadow-2xl active:scale-95 duration-300 hover:shadow-indigo-500/20"
                >
                    {isLoading ? (
                        <div className="flex items-center gap-4">
                            <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                            <span>Synthesizing Components...</span>
                        </div>
                    ) : (
                        <span>Generate Intelligence Suite</span>
                    )}
                </button>
            </div>
        </form>
    );
};

export default ReportBuilder;